import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Eye, 
  EyeOff, 
  Mail, 
  Lock, 
  User, 
  Calendar,
  AlertCircle, 
  CheckCircle,
  ArrowRight,
  ArrowLeft
} from 'lucide-react';
import { RootState } from '../store';
import { registerStart, registerSuccess, registerFailure, clearError } from '../store/slices/authSlice';
import i18n from '../i18n';
import { clientAuthApi } from '../services/clientAuthApi';
import { clientApi } from '../services/clientApi';
import { ClientRegisterRequest } from '@digitaltattoo/types';
import { LegalPrivacyLabel, LegalTermsLabel } from '../components/LegalConsentLabels';
import { InternationalPhoneInput } from '../components/InternationalPhoneInput';
import { isMinor, type SupportedPhoneCountry } from '@digitaltattoo/shared-utils';

function localizeRegisterApiMessage(message: string, t: (key: string) => string): string {
  const source = i18n.getFixedT('fr');
  if (
    message === source('auth.register.apiMessages.phoneExistsEn')
    || message === source('auth.register.apiMessages.phoneExists')
  ) {
    return t('auth.register.errors.accountExistsPhone');
  }
  if (
    message === source('auth.register.apiMessages.emailExistsEn')
    || message === source('auth.register.apiMessages.emailExists')
  ) {
    return t('auth.register.errors.accountExistsEmail');
  }
  return message;
}

export function RegisterPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { loading, error, isAuthenticated } = useSelector((state: RootState) => state.auth);
  
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<ClientRegisterRequest>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    dateOfBirth: '',
    isMinor: null,
    acceptTerms: false,
    acceptPrivacy: false,
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [phoneCountry, setPhoneCountry] = useState<SupportedPhoneCountry>('FR');
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [phoneE164, setPhoneE164] = useState<string | null>(null);
  const [phoneIsValid, setPhoneIsValid] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/account', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Clear error when component mounts
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleInputChange = (
    field: keyof ClientRegisterRequest,
    value: string | boolean | null,
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error for this field
    if (validationErrors[field]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateStep = (step: number): boolean => {
    const errors: Record<string, string> = {};

    if (step === 1) {
      if (!formData.firstName.trim()) {
        errors.firstName = t('auth.register.errors.required');
      }
      if (!formData.email.trim()) {
        errors.email = t('auth.register.errors.required');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = t('auth.register.errors.invalidEmail');
      }
      if (phoneDisplay.trim() && (!phoneIsValid || !phoneE164)) {
        errors.phone = t('auth.register.errors.invalidPhone');
      }
    }

    if (step === 2) {
      if (!formData.password) {
        errors.password = t('auth.register.errors.required');
      } else if (formData.password.length < 8) {
        errors.password = t('auth.register.errors.passwordWeak');
      } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/.test(formData.password)) {
        errors.password = t('auth.register.errors.passwordWeak');
      }
      
      if (!confirmPassword) {
        errors.confirmPassword = t('auth.register.errors.required');
      } else if (formData.password !== confirmPassword) {
        errors.confirmPassword = t('auth.register.errors.passwordMismatch');
      }

      if (formData.dateOfBirth) {
        const birthDate = new Date(formData.dateOfBirth);
        const today = new Date();
        const age = today.getFullYear() - birthDate.getFullYear();
        if (age < 13 || age > 120) {
          errors.dateOfBirth = t('auth.register.errors.invalidDate');
        }
      }
    }

    if (step === 3) {
      if (!formData.acceptTerms) {
        errors.acceptTerms = t('auth.register.errors.termsRequired');
      }
      if (!formData.acceptPrivacy) {
        errors.acceptPrivacy = t('auth.register.errors.privacyRequired');
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const checkDuplicatesForStep1 = async (): Promise<boolean> => {
    const email = formData.email.toLowerCase().trim();
    const phone = phoneE164 ?? '';

    setCheckingDuplicates(true);
    try {
      const response = await clientApi.checkDuplicate({ email, phone });
      if (!response.exists) {
        return true;
      }

      const fields = response.fields?.length
        ? response.fields
        : response.field
          ? [response.field]
          : [];

      const errors: Record<string, string> = {};
      if (fields.includes('email')) {
        errors.email = t('auth.register.errors.accountExistsEmail');
      }
      if (fields.includes('phone')) {
        errors.phone = t('auth.register.errors.accountExistsPhone');
      }
      if (fields.includes('email') && fields.includes('phone')) {
        errors.duplicate = t('auth.register.errors.accountExistsBoth');
      } else if (!errors.email && !errors.phone) {
        errors.duplicate = t('auth.register.errors.accountExistsLogin');
      }

      setValidationErrors((prev) => ({ ...prev, ...errors }));
      return false;
    } catch (error) {
      console.error('Duplicate check error:', error);
      setValidationErrors((prev) => ({
        ...prev,
        duplicate: t('auth.register.errors.duplicateCheckFailed'),
      }));
      return false;
    } finally {
      setCheckingDuplicates(false);
    }
  };

  const nextStep = async () => {
    if (!validateStep(currentStep)) {
      return;
    }

    if (currentStep === 1) {
      const canProceed = await checkDuplicatesForStep1();
      if (!canProceed) {
        return;
      }
    }

    setCurrentStep((prev) => Math.min(prev + 1, 3));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep(3)) {
      return;
    }

    dispatch(registerStart());

    try {
      const response = await clientAuthApi.register({
        ...formData,
        phone: phoneE164 ?? '',
      });
      
      if (response.success) {
        dispatch(registerSuccess({
          user: response.user!,
          requiresEmailVerification: response.requiresEmailVerification || false,
        }));
        setRegistrationSuccess(true);
      } else {
        dispatch(registerFailure(
          localizeRegisterApiMessage(response.message || '', t) || t('auth.register.errors.failed'),
        ));
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error instanceof Error
        ? localizeRegisterApiMessage(error.message, t)
        : t('auth.register.errors.failed');
      dispatch(registerFailure(errorMessage));
    }
  };

  if (registrationSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex items-center justify-center w-16 h-16 bg-green-100 rounded-2xl mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-3xl font-display font-bold text-textPrimary">
              {t('auth.register.success.title')}
            </h2>
            <p className="mt-2 text-sm text-textSecondary">
              {t('auth.register.success.message')}
            </p>
            <div className="mt-6 space-y-4">
              <Link
                to="/login"
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
              >
                {t('auth.register.success.goToLogin')}
              </Link>
              <Link
                to="/"
                className="w-full flex justify-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
              >
                {t('auth.register.success.backToHome')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-accent-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-300 to-accent-500 rounded-2xl mx-auto mb-6">
            <User className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl font-display font-bold text-textPrimary">
            {t('auth.register.title')}
          </h2>
          <p className="mt-2 text-sm text-textSecondary">
            {t('auth.register.subtitle')}
          </p>
          
          {/* Progress Steps */}
          <div className="mt-6 flex items-center justify-center space-x-4">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step <= currentStep
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step}
                </div>
                {step < 3 && (
                  <div
                    className={`w-8 h-0.5 ${
                      step < currentStep ? 'bg-primary-500' : 'bg-gray-200'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {/* Step 1: Personal Information */}
          {currentStep === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium text-textPrimary mb-2">
                    {t('auth.register.firstName')}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-textSecondary" />
                    </div>
                    <input
                      id="firstName"
                      name="firstName"
                      type="text"
                      autoComplete="given-name"
                      required
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                      className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                        validationErrors.firstName
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300 bg-white'
                      }`}
                      placeholder={t('auth.register.firstName')}
                    />
                  </div>
                  {validationErrors.firstName && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {validationErrors.firstName}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium text-textPrimary mb-2">
                    {t('auth.register.lastName')} (optionnel)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-textSecondary" />
                    </div>
                    <input
                      id="lastName"
                      name="lastName"
                      type="text"
                      autoComplete="family-name"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                      className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                        validationErrors.lastName
                          ? 'border-red-300 bg-red-50'
                          : 'border-gray-300 bg-white'
                      }`}
                      placeholder={t('auth.register.lastName')}
                    />
                  </div>
                  {validationErrors.lastName && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {validationErrors.lastName}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-textPrimary mb-2">
                  {t('auth.register.email')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-textSecondary" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                      validationErrors.email
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300 bg-white'
                    }`}
                    placeholder={t('auth.register.email')}
                  />
                </div>
                {validationErrors.email && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {validationErrors.email}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-textPrimary mb-2">
                  {t('auth.register.phone')} (optionnel)
                </label>
                <InternationalPhoneInput
                  id="phone"
                  name="phone"
                  autoComplete="tel"
                  value={phoneDisplay}
                  country={phoneCountry}
                  onChange={(result) => {
                    setPhoneDisplay(result.displayValue);
                    setPhoneCountry(result.country);
                    setPhoneE164(result.e164Value);
                    setPhoneIsValid(result.isValid);
                    setFormData((prev) => ({ ...prev, phone: result.displayValue }));

                    if (result.isValid && validationErrors.phone) {
                      setValidationErrors((prev) => {
                        const next = { ...prev };
                        delete next.phone;
                        return next;
                      });
                    }
                  }}
                  selectClassName="block w-full pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent border-gray-300 bg-white"
                  inputClassName={`block w-full pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                    validationErrors.phone
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-300 bg-white'
                  }`}
                  placeholder={t('auth.register.phone')}
                  error={validationErrors.phone}
                />
              </div>

              {validationErrors.duplicate && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800 flex items-start">
                    <AlertCircle className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                    <span>
                      {validationErrors.duplicate}{' '}
                      <Link to="/login" className="fo-link font-medium">
                        {t('auth.register.errors.loginInstead')}
                      </Link>
                    </span>
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Security */}
          {currentStep === 2 && (
            <div className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-textPrimary mb-2">
                  {t('auth.register.password')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-textSecondary" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className={`block w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                      validationErrors.password
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300 bg-white'
                    }`}
                    placeholder={t('auth.register.password')}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-textSecondary hover:text-textPrimary" />
                    ) : (
                      <Eye className="h-5 w-5 text-textSecondary hover:text-textPrimary" />
                    )}
                  </button>
                </div>
                {validationErrors.password && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {validationErrors.password}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-textPrimary mb-2">
                  {t('auth.register.confirmPassword')}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-textSecondary" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`block w-full pl-10 pr-12 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                      validationErrors.confirmPassword
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300 bg-white'
                    }`}
                    placeholder={t('auth.register.confirmPassword')}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5 text-textSecondary hover:text-textPrimary" />
                    ) : (
                      <Eye className="h-5 w-5 text-textSecondary hover:text-textPrimary" />
                    )}
                  </button>
                </div>
                {validationErrors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {validationErrors.confirmPassword}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="dateOfBirth" className="block text-sm font-medium text-textPrimary mb-2">
                  {t('auth.register.dateOfBirth')} (optionnel)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar className="h-5 w-5 text-textSecondary" />
                  </div>
                  <input
                    id="dateOfBirth"
                    name="dateOfBirth"
                    type="date"
                    disabled={formData.isMinor === true && !formData.dateOfBirth}
                    value={formData.dateOfBirth}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        dateOfBirth: value,
                        isMinor: value ? isMinor(value) : null,
                      }));
                    }}
                    className={`block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                      validationErrors.dateOfBirth
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300 bg-white'
                    }`}
                  />
                </div>
                {validationErrors.dateOfBirth && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {validationErrors.dateOfBirth}
                  </p>
                )}
                <label className="mt-3 flex items-center gap-2 text-sm text-textPrimary">
                  <input
                    type="checkbox"
                    checked={Boolean(formData.dateOfBirth ? isMinor(formData.dateOfBirth) : formData.isMinor)}
                    disabled={Boolean(formData.dateOfBirth)}
                    onChange={(e) => handleInputChange(
                      'isMinor',
                      e.target.checked ? true : null,
                    )}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600"
                  />
                  Mineur (optionnel)
                </label>
              </div>
            </div>
          )}

          {/* Step 3: Terms & Conditions */}
          {currentStep === 3 && (
            <div className="space-y-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-medium text-textPrimary mb-2">{t('auth.register.terms.title')}</h3>
                <p className="text-sm text-textSecondary mb-4">
                  {t('auth.register.terms.description')}
                </p>
                
                <div className="space-y-3">
                  <div className="flex items-start">
                    <input
                      id="acceptTerms"
                      name="acceptTerms"
                      type="checkbox"
                      checked={formData.acceptTerms}
                      onChange={(e) => handleInputChange('acceptTerms', e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
                    />
                    <label htmlFor="acceptTerms" className="ml-3 block text-sm text-textSecondary">
                      <LegalTermsLabel />
                    </label>
                  </div>
                  {validationErrors.acceptTerms && (
                    <p className="text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {validationErrors.acceptTerms}
                    </p>
                  )}

                  <div className="flex items-start">
                    <input
                      id="acceptPrivacy"
                      name="acceptPrivacy"
                      type="checkbox"
                      checked={formData.acceptPrivacy}
                      onChange={(e) => handleInputChange('acceptPrivacy', e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mt-1"
                    />
                    <label htmlFor="acceptPrivacy" className="ml-3 block text-sm text-textSecondary">
                      <LegalPrivacyLabel />
                    </label>
                  </div>
                  {validationErrors.acceptPrivacy && (
                    <p className="text-sm text-red-600 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {validationErrors.acceptPrivacy}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            {currentStep > 1 ? (
              <button
                type="button"
                onClick={prevStep}
                className="flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('auth.register.previous')}
              </button>
            ) : (
              <div />
            )}

            {currentStep < 3 ? (
              <button
                type="button"
                onClick={nextStep}
                disabled={checkingDuplicates}
                className="flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {checkingDuplicates ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('auth.register.checking')}
                  </>
                ) : (
                  <>
                    {t('auth.register.next')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {t('auth.register.processing')}
                  </>
                ) : (
                  <>
                    {t('auth.register.registerButton')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </button>
            )}
          </div>

          {/* Login Link */}
          <div className="text-center">
            <p className="text-sm text-textSecondary">
              {t('auth.register.hasAccount')}{' '}
              <Link
                to="/login"
                className="fo-link font-medium"
              >
                {t('auth.register.loginLink')}
              </Link>
            </p>
          </div>
        </form>

        {/* Back to Home */}
        <div className="text-center">
          <Link
            to="/"
            className="text-sm text-textSecondary hover:text-textPrimary transition-colors"
          >
            {t('auth.register.backToHome')}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
